import { FC, useEffect, useMemo, useState } from 'react'
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  DialogContent,
  FormHelperText,
  Stack
} from '@mui/material'
import { useFormik } from 'formik'
import * as yup from 'yup'
import dayjs, { Dayjs } from 'dayjs'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  CubeTransparentIcon,
  Squares2X2Icon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import {
  CloseButton,
  CustomizedDialog,
  StepButton,
  StepTitle,
  StyledAccordion,
  StyledDialogTitle,
  StyledDialogActions
} from '../customize-report-dialog/customizeReportDialog.styles'
import {
  InfoCard,
  InfoCardInner,
  InfoTitle,
  InfoDescription,
  DetailsAccordion,
  DetailsLabel,
  ScopeRow,
  ScopeIconWrapper,
  ScopeLabel,
  ScopeValue
} from './scheduleReportDialog.styles'
import {
  ReportScheduleInfo,
  ReportTemplateResponse,
  ScheduleInterval,
  StartReportScheduleRequest
} from 'models'
import { CpiServiceV3, FrameworkServiceV3, ReportTemplateService } from 'services'
import { useAppDispatch } from 'store/hooks'
import { closeToast, openToast, ToastVariant } from 'store/toasts/toastSlice'
import SingleDatePicker from 'ui/components/single-date-picker/SingleDatePicker'
import SelectV2 from 'ui/components/form-components-v2/select-v2'
import RecipientsPicker from '../recipients-picker/RecipientsPicker'
import ThemeButton from 'ui/components/buttons/primary/ThemeButton'
import theme from 'theme'

enum Step {
  None = -1,
  Schedule = 0,
  Recipients = 1
}

const IS_DEV_OR_STAGING = ['development', 'staging'].includes(process.env.REACT_APP_NODE_ENV as string)

const INTERVAL_OPTIONS = [
  ...(IS_DEV_OR_STAGING
    ? [
        { label: '1H', value: ScheduleInterval.ONE_HOUR },
        { label: '1D', value: ScheduleInterval.ONE_DAY }
      ]
    : []),
  { label: '1W', value: ScheduleInterval.WEEKLY },
  { label: '1M', value: ScheduleInterval.MONTHLY },
  { label: '3M', value: ScheduleInterval.QUARTERLY },
  { label: '6M', value: ScheduleInterval.SEMI_ANNUAL },
  { label: '1Y', value: ScheduleInterval.YEARLY }
]

interface ScopeData {
  frameworkName: string
  categories: { id: string; name: string }[]
  cpis: { id: string; name: string }[]
  timeFrame: string
  frequency: string
}

interface ScheduleReportDialogProps {
  isOpen: boolean
  onClose: () => void
  template: ReportTemplateResponse | null
  onScheduled: (updated: ReportTemplateResponse) => void
  onRequestStop: () => void
}

const ScheduleReportDialog: FC<ScheduleReportDialogProps> = ({
  isOpen,
  onClose,
  template,
  onScheduled,
  onRequestStop
}) => {
  const dispatch = useAppDispatch()
  const isEditMode = Boolean(template?.schedule)
  const existingSchedule: ReportScheduleInfo | undefined = template?.schedule ?? undefined

  const [activeStep, setActiveStep] = useState<Step>(Step.None)
  const [detailsExpanded, setDetailsExpanded] = useState(false)
  const [scopeData, setScopeData] = useState<ScopeData | null>(null)
  const [isScopeLoading, setIsScopeLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const formik = useFormik<{
    startDate: Dayjs | null
    interval: ScheduleInterval | ''
    recipients: string[]
  }>({
    enableReinitialize: true,
    initialValues: {
      startDate: existingSchedule ? dayjs(existingSchedule.start_date) : null,
      interval: existingSchedule?.interval ?? '',
      recipients: existingSchedule?.recipients ?? []
    },
    validationSchema: yup.object({
      startDate: yup.mixed().required('Required'),
      interval: yup.string().required('Required'),
      recipients: yup.array().min(1, 'At least one recipient is required')
    }),
    onSubmit: async (values) => {
      if (!template) return
      const request: StartReportScheduleRequest = {
        interval: values.interval as ScheduleInterval,
        start_date: (values.startDate as Dayjs).format('YYYY-MM-DD'),
        recipients: values.recipients
      }
      try {
        setIsSubmitting(true)
        const updated = await ReportTemplateService.startSchedule(template.id, request)
        dispatch(
          openToast({
            variant: ToastVariant.success,
            props: {
              text: isEditMode ? 'Schedule Updated' : 'Schedule Created',
              description: ''
            }
          })
        )
        setTimeout(() => dispatch(closeToast()), 5000)
        onScheduled(updated)
        handleClose()
      } catch {
        dispatch(
          openToast({
            variant: ToastVariant.error,
            props: {
              text: isEditMode ? 'Failed to Update Schedule' : 'Failed to Create Schedule',
              description: 'Please contact our support team.'
            }
          })
        )
        setTimeout(() => dispatch(closeToast()), 5000)
      } finally {
        setIsSubmitting(false)
      }
    }
  })

  const handleClose = () => {
    onClose()
    setActiveStep(Step.None)
    setDetailsExpanded(false)
    formik.resetForm()
  }

  const inactiveStepSx = {
    backgroundColor: 'transparent !important',
    border: `1px solid ${theme.colors.tooltipBg} !important`,
    color: `${theme.colors.dropdownLabel} !important`
  }

  useEffect(() => {
    if (!isOpen || !template?.config) {
      setScopeData(null)
      return
    }
    let cancelled = false
    const fetchScope = async () => {
      try {
        setIsScopeLoading(true)
        const config = template.config

        let frameworkName = config.framework || '-'
        const isStandardFramework = ['nist', 'onyxia'].includes(config.framework)
        if (!isStandardFramework && config.framework) {
          try {
            const customFramework = await FrameworkServiceV3.getCustomFramework()
            frameworkName = customFramework.title || config.framework
          } catch {
            // keep slug
          }
        }

        let fwCategories: any[] = []
        try {
          fwCategories = await FrameworkServiceV3.searchFrameworkCategories({ framework: config.framework })
        } catch {
          fwCategories = []
        }

        const categories: { id: string; name: string }[] = []
        const cpis: { id: string; name: string }[] = []
        if (config.categories && config.categories.length > 0) {
          config.categories.forEach((cat) => {
            if (cat.metrics.length === 0) {
              const found = fwCategories.find((c: any) => c.id === cat.id)
              categories.push({ id: cat.id, name: found?.title || cat.id })
            }
          })
          const allCpiMetrics: string[] = []
          config.categories.forEach((cat) => {
            if (cat.metrics.length > 0) allCpiMetrics.push(...cat.metrics)
          })
          if (allCpiMetrics.length > 0) {
            const results = await Promise.allSettled(allCpiMetrics.map((id) => CpiServiceV3.getCpiDetails(id)))
            results.forEach((result, idx) => {
              const id = allCpiMetrics[idx]
              if (result.status === 'fulfilled') {
                cpis.push({ id, name: result.value.display_name || id })
              } else {
                cpis.push({ id, name: id })
              }
            })
          }
        }

        const timeFrame =
          config.timePeriod ||
          (config.dateFrom && config.dateTo ? `${config.dateFrom} to ${config.dateTo}` : '-')
        const frequency = config.frequency
          ? config.frequency.charAt(0).toUpperCase() + config.frequency.slice(1)
          : '-'

        if (!cancelled) {
          setScopeData({ frameworkName, categories, cpis, timeFrame, frequency })
        }
      } catch {
        if (!cancelled) setScopeData(null)
      } finally {
        if (!cancelled) setIsScopeLoading(false)
      }
    }
    fetchScope()
    return () => {
      cancelled = true
    }
  }, [isOpen, template])

  const recipientsError = formik.touched.recipients && Boolean(formik.errors.recipients)

  const isSubmitDisabled =
    isSubmitting ||
    !formik.values.startDate ||
    !formik.values.interval ||
    formik.values.recipients.length === 0

  const handleStepChange = (step: Step) => {
    setActiveStep((prev) => (prev === step ? Step.None : step))
  }

  const categoryDisplay = useMemo(() => {
    if (!scopeData) return null
    if (scopeData.categories.length === 0 && scopeData.cpis.length === 0) {
      return <ScopeValue>All Categories</ScopeValue>
    }
    return (
      <Box>
        {(scopeData.categories.length > 0 || scopeData.cpis.length === 0) && (
          <ScopeValue>
            <Box component="span" sx={{ color: theme.colors.dropdownLabel, mr: 0.5, fontWeight: theme.typography.fontWeight.normal }}>
              Category:
            </Box>
            {scopeData.categories.length > 0 ? scopeData.categories[0].name : 'All Categories'}
            {scopeData.categories.length > 1 && `, +${scopeData.categories.length - 1}`}
          </ScopeValue>
        )}
        {scopeData.cpis.length > 0 && (
          <ScopeValue>
            <Box component="span" sx={{ color: theme.colors.dropdownLabel, mr: 0.5, fontWeight: theme.typography.fontWeight.normal }}>
              CPI:
            </Box>
            {scopeData.cpis[0].name}
            {scopeData.cpis.length > 1 && `, +${scopeData.cpis.length - 1}`}
          </ScopeValue>
        )}
      </Box>
    )
  }, [scopeData])

  if (!template) return null

  return (
    <CustomizedDialog open={isOpen} onClose={handleClose} maxWidth={false} disableRestoreFocus>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <StyledDialogTitle>{isEditMode ? 'Edit Schedule' : 'Schedule Report'}</StyledDialogTitle>
        <CloseButton onClick={handleClose}>
          <CloseIcon />
        </CloseButton>
      </Stack>
      <DialogContent>
        <Stack direction="column" spacing={1}>
          <InfoCard>
            <InfoCardInner>
              <InfoTitle>{template.name}</InfoTitle>
              {template.description && <InfoDescription>{template.description}</InfoDescription>}
            </InfoCardInner>
            <DetailsAccordion
              expanded={detailsExpanded}
              onChange={() => setDetailsExpanded((v) => !v)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <DetailsLabel>Details</DetailsLabel>
              </AccordionSummary>
              <AccordionDetails>
                {isScopeLoading ? (
                  <Box display="flex" justifyContent="center" py={2} sx={{ width: '100%' }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <>
                    <ScopeRow>
                      <ScopeIconWrapper>
                        <CubeTransparentIcon width={18} height={18} stroke={theme.colors.dustyRoseGray} />
                      </ScopeIconWrapper>
                      <Box>
                        <ScopeLabel>Framework</ScopeLabel>
                        <ScopeValue sx={{ textTransform: 'capitalize' }}>
                          {scopeData?.frameworkName || '-'}
                        </ScopeValue>
                      </Box>
                    </ScopeRow>
                    <ScopeRow>
                      <ScopeIconWrapper>
                        <Squares2X2Icon width={18} height={18} stroke={theme.colors.dustyRoseGray} />
                      </ScopeIconWrapper>
                      <Box>
                        <ScopeLabel>Category & CPIs</ScopeLabel>
                        {categoryDisplay}
                      </Box>
                    </ScopeRow>
                    <ScopeRow>
                      <ScopeIconWrapper>
                        <ClockIcon width={18} height={18} stroke={theme.colors.dustyRoseGray} />
                      </ScopeIconWrapper>
                      <Box>
                        <ScopeLabel>Time frame</ScopeLabel>
                        <ScopeValue sx={{ textTransform: 'uppercase' }}>{scopeData?.timeFrame || '-'}</ScopeValue>
                      </Box>
                    </ScopeRow>
                    <ScopeRow>
                      <ScopeIconWrapper>
                        <ArrowPathIcon width={18} height={18} stroke={theme.colors.dustyRoseGray} />
                      </ScopeIconWrapper>
                      <Box>
                        <ScopeLabel>Frequency</ScopeLabel>
                        <ScopeValue>{scopeData?.frequency || '-'}</ScopeValue>
                      </Box>
                    </ScopeRow>
                  </>
                )}
              </AccordionDetails>
            </DetailsAccordion>
          </InfoCard>

          <StyledAccordion
            expanded={activeStep === Step.Schedule}
            onChange={() => handleStepChange(Step.Schedule)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={2} alignItems="center">
                <StepButton sx={activeStep === Step.Schedule ? undefined : inactiveStepSx}>1</StepButton>
                <StepTitle>Schedule</StepTitle>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <Box sx={{ flex: 1 }}>
                  <SingleDatePicker
                    label="Start date"
                    value={formik.values.startDate}
                    minDate={dayjs().startOf('day')}
                    onChange={(val) => formik.setFieldValue('startDate', val)}
                    wrapperWidth="100%"
                    inputWidth="100%"
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    '& .MuiSelect-select': {
                      color: !formik.values.interval ? theme.colors.devicesSecondaryText : theme.colors.menuItemText
                    }
                  }}
                >
                  <SelectV2
                    id="interval"
                    label="Repeat every"
                    options={INTERVAL_OPTIONS}
                    value={formik.values.interval || '__placeholder__'}
                    onChange={(e) => {
                      const v = e.target.value as string
                      formik.setFieldValue('interval', v === '__placeholder__' ? '' : v)
                    }}
                    error={formik.touched.interval && Boolean(formik.errors.interval)}
                    helperText={formik.touched.interval && formik.errors.interval}
                    renderValue={(value) => {
                      if (!value || value === '__placeholder__') return 'Select'
                      const selected = INTERVAL_OPTIONS.find((o) => o.value === value)
                      return selected?.label || 'Select'
                    }}
                  />
                </Box>
              </Stack>
            </AccordionDetails>
          </StyledAccordion>

          <StyledAccordion
            expanded={activeStep === Step.Recipients}
            onChange={() => handleStepChange(Step.Recipients)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={2} alignItems="center">
                <StepButton sx={activeStep === Step.Recipients ? undefined : inactiveStepSx}>2</StepButton>
                <StepTitle>Recipients</StepTitle>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <RecipientsPicker
                selectedIds={formik.values.recipients}
                onChange={(ids) => formik.setFieldValue('recipients', ids)}
              />
              {recipientsError && (
                <FormHelperText error sx={{ mt: 1, color: `${theme.colors.errorLight} !important` }}>
                  {formik.errors.recipients as string}
                </FormHelperText>
              )}
            </AccordionDetails>
          </StyledAccordion>
        </Stack>
      </DialogContent>
      <StyledDialogActions>
        {isEditMode ? (
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
            <ThemeButton themevariant="secondary" shape="rectangular" onClick={handleClose}>
              Cancel
            </ThemeButton>
            <Stack direction="row" spacing={1}>
              <ThemeButton themevariant="secondary" shape="rectangular" onClick={onRequestStop}>
                Stop Schedule
              </ThemeButton>
              <ThemeButton
                themevariant="primary"
                shape="rectangular"
                type="button"
                onClick={() => formik.handleSubmit()}
                disabled={isSubmitDisabled}
              >
                {isSubmitting ? <CircularProgress size={18} color="primary" /> : 'Save'}
              </ThemeButton>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
            <ThemeButton themevariant="secondary" shape="rectangular" onClick={handleClose} sx={{ flex: 1 }}>
              Cancel
            </ThemeButton>
            <ThemeButton
              themevariant="primary"
              shape="rectangular"
              type="button"
              onClick={() => formik.handleSubmit()}
              disabled={isSubmitDisabled}
              sx={{ flex: 1 }}
            >
              {isSubmitting ? <CircularProgress size={18} color="primary" /> : 'Schedule'}
            </ThemeButton>
          </Stack>
        )}
      </StyledDialogActions>
    </CustomizedDialog>
  )
}

export default ScheduleReportDialog
